import * as React from 'react';
import { isUndefined, maxBy } from 'lodash';
import parse from 'html-react-parser';
import * as sanitizeHtml from 'sanitize-html';

import {
  IControlError,
  IControlSchema,
  IControlSchemaBase,
} from '../../core/Controls';
import { CheckboxSetBlockValidate } from '../../core/validation/validate';

import { Component, I18NString } from '../helpers/Utils';
import { IDispatcher } from '../helpers/Dispatcher';

import Field from './components/Field';
import ErrorAlert from './components/ErrorAlert';

interface ICheckBoxSetButtonOption {
  label: I18NString;
  name: string;
  group: string;
  checked?: boolean;
  disabled?: boolean;
}

export interface ICheckBoxSetButtonSchema extends IControlSchemaBase {
  value?: any;
  options: Array<ICheckBoxSetButtonOption>;
  disabled?: boolean;
  description: I18NString;
  notification: I18NString;
  possibilityOfChoiceOptions?: 'all' | 'group';
  defaultValue?: any;
  minSelected?: number;
  maxSelected?: number;
  extra: any;
}

interface ICheckBoxSetButtonProps {
  schema: ICheckBoxSetButtonSchema;
  api: any;
  dispatcher?: IDispatcher;
  error: IControlError | undefined;
  visited: boolean;
}

interface State {
  value: any;
  isOpen: boolean;
  isOpenNotification: boolean;
}

class CheckBoxSetButton extends Component<ICheckBoxSetButtonProps, State> {
  state = {
    value: null,
    isOpen: false,
    isOpenNotification: true,
  };

  componentDidMount() {
    const { value, defaultValue } = this.props.schema;
    if (!isUndefined(value)) {
      this.setState({
        value,
      });
    } else if (defaultValue) {
      this.setState({ value: defaultValue });
    }
  }

  // TODO: не используется !?
  onClick = (event: any) => {
    const { attributes } = event.target;
    const { value } = attributes.getNamedItem('data-value');
    this.setState({
      value,
    });
    this.props.dispatcher.onBlur(this.props.schema.id, value, true, undefined);
  };

  onOpenHelp = () => {
    this.setState(({ isOpen }) => ({ isOpen: !isOpen }));
  };

  onOpenNotification = () => {
    this.setState(({ isOpenNotification }) => ({
      isOpenNotification: !isOpenNotification,
    }));
  };

  getCheckState = (name: string) => {
    const { value } = this.state;
    if (value) {
      if (this.state.value.indexOf(name) > -1) return true;
    }

    return false;
  };

  setCheckState = (name: string, _event: any) => {
    const { value } = this.state;
    const {
      schema: { options },
    } = this.props;
    let newValue = [];
    if (!value || typeof value === 'undefined') {
      newValue = [name];
    }
    try {
      const currValue = value;
      if (currValue.indexOf(name) > -1) {
        currValue.splice(currValue.indexOf(name), 1);
        newValue = [...currValue];
      } else {
        newValue = [...currValue, name];
      }
    } catch (error_) {
      newValue = [name];
    }
    let i = newValue.length;
    while (i--) {
      // eslint-disable-next-line no-loop-func
      if (options.findIndex((el) => el.name === newValue[i]) === -1) {
        newValue.splice(i, 1);
        i = newValue.length;
      }
    }
    this.setState({
      value: newValue,
    });

    const error = CheckboxSetBlockValidate(newValue, this.props.schema);

    this.props.dispatcher.onBlur(
      this.props.schema.id,
      newValue,
      true,
      error && error.isError ? error : undefined,
    );
  };

  render() {
    const {
      schema: {
        options,
        disabled,
        description,
        notification,
        extra,
        possibilityOfChoiceOptions,
      },
      error,
    } = this.props;
    const { value } = this.state;

    const optionHashMap =
      options &&
      options.reduce(
        (acc, item) => ({
          ...acc,
          [item.name]: item,
        }),
        {},
      );

    const groupCount =
      value &&
      value.reduce(
        // eslint-disable-next-line array-callback-return,consistent-return
        (acc, item) => {
          const value = optionHashMap[item];
          if (value && value.group) {
            return {
              ...acc,
              [value.group]: {
                count: acc?.[value.group] ? acc[value.group].count + 1 : 1,
              },
            };
          }
        },
        {
          *[Symbol.iterator]() {
            const keysObj = Object.keys(this);
            for (const item of keysObj) {
              yield {
                group: item,
                count: this[item].count,
              };
            }
          },
        },
      );

    const namesInputs = options.map((option: ICheckBoxSetButtonOption) => {
      // Правила выбора списка значений
      let disabledOption = disabled;
      if (possibilityOfChoiceOptions === 'group' && groupCount) {
        const whoGroupLeadInChecked = maxBy([...groupCount], 'count');
        disabledOption = whoGroupLeadInChecked
          ? option.group !== whoGroupLeadInChecked.group
          : disabledOption;
      }

      return {
        ...option,
        name: `${option.name}`,
        checked: this.getCheckState(option.name),
        disabled: disabledOption,
      };
    });

    return (
      <Field
        {...this.props.schema}
        status={this.state.value ? 'success' : 'default'}
      >
        {extra && extra.hint && (
          <div className="alert alert-info hint">
            {parse(sanitizeHtml(extra.hint))}
          </div>
        )}
        <div className="input-group-cr checkset">
          <div className="btn-group">
            {namesInputs.map(
              (option: ICheckBoxSetButtonOption, idx: number) => (
                <label
                  key={idx}
                  htmlFor={option.name}
                  className="checkset-inline"
                >
                  <input
                    type="checkbox"
                    disabled={option.disabled}
                    name={option.name}
                    id={option.name}
                    data-value={option.label}
                    checked={option.checked}
                    onChange={this.setCheckState.bind(this, option.name)}
                  />
                  {option.label}
                </label>
              ),
            )}
          </div>
          {notification && (
            <span className="btn btn-default" onClick={this.onOpenNotification}>
              <span className="glyphicon glyphicon-info-sign" />
            </span>
          )}
          {description && (
            <span className="btn btn-default" onClick={this.onOpenHelp}>
              <span className="glyphicon glyphicon-question-sign" />
            </span>
          )}
        </div>
        {notification && this.state.isOpenNotification && (
          <div className="alert alert-warning" role="alert">
            {this.tr(notification)}
          </div>
        )}
        {description && this.state.isOpen && (
          <div className="alert alert-warning" role="alert">
            {this.tr(description)}
          </div>
        )}
        {error && error.isError && <ErrorAlert errorMessage={error.message} />}
      </Field>
    );
  }
}

export function createCheckBoxSet(
  schema: IControlSchema,
  api: any,
  dispatcher: IDispatcher = null,
  error: IControlError | undefined,
  visited: boolean,
): React.ReactElement<any> {
  return (
    <CheckBoxSetButton
      schema={schema as ICheckBoxSetButtonSchema}
      api={api}
      error={error}
      visited={visited}
      dispatcher={dispatcher}
    />
  );
}
